import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { MutationFetchFunction } from 'common/helpers/common';
import { ApiError, ApiResult } from 'common/repositories/common.model';

import {
  TicketSaleModel,
  TicketSaleMutationInput,
  TicketSaleDeleteInput,
  TicketSaleUpdateInput,
  OpenTicketPatchInput,
  FlightSaleReschedulePutInput,
} from './model';

export function useCreateTicketSale(
  options?: UseMutationOptions<
    ApiResult<TicketSaleModel>,
    ApiError,
    TicketSaleMutationInput
  >,
): UseMutationResult<
  ApiResult<TicketSaleModel>,
  ApiError,
  TicketSaleMutationInput
> {
  return useMutation<
    ApiResult<TicketSaleModel>,
    ApiError,
    TicketSaleMutationInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: 'flight-sales',
      method: 'POST',
      classType: TicketSaleModel,
      body,
    });
  }, options);
}

export function useUpdateTicketSale(
  options?: UseMutationOptions<
    ApiResult<TicketSaleModel>,
    ApiError,
    TicketSaleUpdateInput
  >,
): UseMutationResult<
  ApiResult<TicketSaleModel>,
  ApiError,
  TicketSaleUpdateInput
> {
  return useMutation<
    ApiResult<TicketSaleModel>,
    ApiError,
    TicketSaleUpdateInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: `flight-sales/${body.ticketSaleId}`,
      method: 'PUT',
      classType: TicketSaleModel,
      body: body.body,
    });
  }, options);
}

export function useRescheduleTicketSale(
  options?: UseMutationOptions<
    ApiResult<TicketSaleModel>,
    ApiError,
    FlightSaleReschedulePutInput
  >,
): UseMutationResult<
  ApiResult<TicketSaleModel>,
  ApiError,
  FlightSaleReschedulePutInput
> {
  return useMutation<
    ApiResult<TicketSaleModel>,
    ApiError,
    FlightSaleReschedulePutInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: `flight-sales/${body.flightSaleId}/reschedule`,
      method: 'PUT',
      body: body.body,
    });
  }, options);
}

export function useDeleteTicketSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    TicketSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, TicketSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, TicketSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `flight-sales/${body.id}`,
        method: 'DELETE',
      });
    },
    options,
  );
}

export function useVoidTicketSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    TicketSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, TicketSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, TicketSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `flight-sales/${body.id}/void`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useOpenTicketTicketSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    OpenTicketPatchInput
  >,
): UseMutationResult<{ message: string }, ApiError, OpenTicketPatchInput> {
  return useMutation<{ message: string }, ApiError, OpenTicketPatchInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `flight-sales/${body.flightSaleRefId}/open-ticket`,
        method: 'PATCH',
        body: body.body,
      });
    },
    options,
  );
}

export function useCompleteTicketSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    TicketSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, TicketSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, TicketSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `flight-sales/${body.id}/complete`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useRevertCompleteTicketSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    TicketSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, TicketSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, TicketSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `flight-sales/${body.id}/revert-complete`,
        method: 'PATCH',
      });
    },
    options,
  );
}
